import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  LogOut,
  Settings as SettingsIcon,
  FlaskConical,
  BarChart3,
  Wallet,
  Menu,
  X,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Users,
  Network,
  ClipboardCheck,
  FileSearch,
  KeyRound,
  ShieldCheck,
  Building2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { DashboardBreadcrumbProvider } from './DashboardBreadcrumbContext';
import { LogoMark } from './Logo';

const linkBase = 'flex items-center gap-2 px-3 py-2 text-sm border border-transparent rounded-sm transition-colors';
const linkActive = 'bg-zinc-900 text-white border-zinc-900';
const linkIdle = 'text-zinc-700 hover:bg-zinc-100';
const INTERNAL_SIDEBAR_COLLAPSED_KEY = 'idemgo.internalSidebarCollapsed';
const APP_CANVAS_BG = 'bg-white';

const NAV_SECTIONS = [
  {
    label: 'Workspace ops',
    items: [
      {
        to: '/internal/accounts',
        end: true,
        label: 'Accounts Overview',
        icon: Building2,
        testid: 'internal-nav-accounts-overview',
        activePrefixes: ['/internal/accounts'],
      },
    ],
  },
  {
    label: 'Identity governance',
    items: [
      {
        to: '/internal/identities',
        end: true,
        label: 'Identity Operations',
        icon: Users,
        testid: 'internal-nav-identity-operations',
        activePrefixes: ['/internal/identities'],
      },
      {
        to: '/internal/digital-key-accounts',
        label: 'Digital Key Accounts',
        icon: KeyRound,
        testid: 'internal-nav-digital-key-accounts',
        activePrefixes: ['/internal/digital-key-accounts'],
      },
      {
        to: '/internal/relying-party-profiles',
        label: 'Relying Parties',
        icon: Network,
        testid: 'internal-nav-relying-party-profiles',
        activePrefixes: ['/internal/relying-party-profiles'],
      },
    ],
  },
  {
    label: 'Readiness & evidence',
    items: [
      { to: '/internal/fas-readiness', label: 'FAS Readiness', icon: ClipboardCheck, testid: 'internal-nav-fas-readiness' },
      { to: '/internal/bosa-evidence-pack', label: 'BOSA Evidence Pack', icon: FileSearch, testid: 'internal-nav-bosa-evidence-pack' },
      { to: '/internal/audit-governance-pack', label: 'Audit Governance', icon: ShieldCheck, testid: 'internal-nav-audit-governance-pack' },
      { to: '/internal/partner-technical-readiness', label: 'Partner Readiness', icon: ClipboardCheck, testid: 'internal-nav-partner-technical-readiness' },
      { to: '/internal/regulated-identifiers', label: 'Regulated Identifiers', icon: FileSearch, testid: 'internal-nav-regulated-identifiers' },
    ],
  },
  {
    label: 'Internal lab',
    items: [
      { to: '/internal/digital-key-demo', label: 'Digital Key Demo Runner', icon: ClipboardCheck, testid: 'internal-nav-digital-key-demo' },
      { to: '/internal/digital-key-architecture', label: 'Digital Key Layer', icon: ClipboardCheck, testid: 'internal-nav-digital-key-architecture' },
      { to: '/internal/authenticator-preview', label: 'Authenticator Preview', icon: KeyRound, testid: 'internal-nav-authenticator-preview' },
      { to: '/internal/oidc-lab', label: 'OIDC Lab', icon: FlaskConical, testid: 'internal-nav-oidc-lab' },
    ],
  },
];

function NavLinks({ onNavigate, collapsed = false }) {
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <>
      {NAV_SECTIONS.map((section, sectionIndex) => (
        <div key={section.label} className={`flex flex-col gap-1 ${collapsed ? 'items-center' : ''}`}>
          {section.label && !collapsed && (
            <div className={`px-3 ${sectionIndex === 0 ? 'pt-0' : 'pt-3'} pb-1 text-[10px] uppercase tracking-widest font-mono text-zinc-400`}>
              {section.label}
            </div>
          )}
          {section.items.map(({ to, end, label, icon: Icon, testid, activePrefixes }) => {
            const isActive = activePrefixes
              ? pathname === to || activePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
              : end
                ? pathname === to
                : pathname === to || pathname.startsWith(`${to}/`);

            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                onClick={onNavigate}
                title={collapsed ? label : undefined}
                aria-label={collapsed ? label : undefined}
                className={`${linkBase} ${collapsed ? 'h-9 w-9 justify-center gap-0 px-0 py-0' : ''} ${isActive ? linkActive : linkIdle}`}
              >
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            );
          })}
        </div>
      ))}
    </>
  );
}

function AccountMenu({ user, onLogout, onNavigate, side = 'bottom', align = 'end', className = '' }) {
  const displayName = user?.full_name || user?.name || 'Account';
  const workspaceName = user?.organization_name || 'Workspace';
  const workspaceInitial = workspaceName.trim().charAt(0).toUpperCase() || 'W';
  const role = user?.role || '';

  const go = (path) => {
    onNavigate(path);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`inline-flex min-w-0 items-center justify-between gap-2 border border-zinc-200 rounded-sm bg-white px-2 py-1.5 text-sm text-zinc-700 hover:border-zinc-400 transition-colors ${className}`}
        >
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-sm bg-black font-mono text-[10px] font-semibold uppercase tracking-widest text-white">
            {workspaceInitial}
          </span>
          <span className="truncate">{workspaceName}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" strokeWidth={1.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="w-64 rounded-sm border-zinc-200 bg-white p-1 shadow-sm">
        <div className="px-3 py-2">
          <div className="truncate text-sm font-medium text-zinc-900">{displayName}</div>
          <div className="mt-1 truncate font-mono text-xs text-zinc-500">{user?.email || '—'}</div>
          {role && (
            <span className="mt-2 inline-flex items-center border border-zinc-200 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              {role}
            </span>
          )}
        </div>
        <DropdownMenuSeparator className="bg-zinc-100" />
        <DropdownMenuItem onSelect={() => go('/settings')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <SettingsIcon className="h-4 w-4" strokeWidth={1.5} />
          Organization settings
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go('/settings')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <Users className="h-4 w-4" strokeWidth={1.5} />
          Members
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go('/usage')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <BarChart3 className="h-4 w-4" strokeWidth={1.5} />
          Usage
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => go('/billing')} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <Wallet className="h-4 w-4" strokeWidth={1.5} />
          Billing
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-100" />
        <DropdownMenuItem onSelect={onLogout} className="cursor-pointer text-zinc-700 focus:bg-zinc-50 focus:text-zinc-900">
          <LogOut className="h-4 w-4" strokeWidth={1.5} />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          title="Notifications"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-900"
        >
          <Bell className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-56 rounded-sm border-zinc-200 bg-white p-3 shadow-sm">
        <p className="text-sm text-zinc-500">No notifications yet</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function fallbackBreadcrumb(pathname) {
  if (pathname === '/internal' || pathname === '/internal/') return [{ label: 'Internal', to: '/internal' }];
  if (pathname.startsWith('/internal/accounts/')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Accounts Overview', to: '/internal/accounts' },
  ];
  if (pathname.startsWith('/internal/accounts')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Accounts Overview', to: '/internal/accounts' },
  ];
  if (pathname.startsWith('/internal/identities/')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Identity Detail', to: '/internal' },
  ];
  if (pathname.startsWith('/internal/relying-party-profiles')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Relying Party Profiles', to: '/internal/relying-party-profiles' },
  ];
  if (pathname.startsWith('/internal/fas-readiness')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'FAS Recognition Readiness', to: '/internal/fas-readiness' },
  ];
  if (pathname.startsWith('/internal/bosa-evidence-pack')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'BOSA Evidence Pack', to: '/internal/bosa-evidence-pack' },
  ];
  if (pathname.startsWith('/internal/audit-governance-pack')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Audit Governance', to: '/internal/audit-governance-pack' },
  ];
  if (pathname.startsWith('/internal/partner-technical-readiness')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Partner Technical Readiness', to: '/internal/partner-technical-readiness' },
  ];
  if (pathname.startsWith('/internal/regulated-identifiers')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Regulated Identifier Strategy', to: '/internal/regulated-identifiers' },
  ];
  if (pathname.startsWith('/internal/digital-key-demo')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Digital Key Demo Runner', to: '/internal/digital-key-demo' },
  ];
  if (pathname.startsWith('/internal/digital-key-architecture')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Digital Key Layer Architecture', to: '/internal/digital-key-architecture' },
  ];
  if (pathname.startsWith('/internal/authenticator-preview')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Authenticator Preview', to: '/internal/authenticator-preview' },
  ];
  if (pathname.startsWith('/internal/oidc-lab')) return [
    { label: 'Internal', to: '/internal' },
    { label: 'Private OIDC Lab', to: '/internal/oidc-lab' },
  ];
  return [{ label: 'Internal', to: '/internal' }];
}

function topbarCrumbs(breadcrumb, pathname) {
  if (!breadcrumb) return fallbackBreadcrumb(pathname);
  const crumbs = [];
  if (breadcrumb.showDashboard) crumbs.push({ label: 'Internal', to: '/internal' });
  crumbs.push({ label: breadcrumb.rootLabel, to: breadcrumb.rootTo });
  if (breadcrumb.current) crumbs.push({ label: breadcrumb.current });
  return crumbs;
}

function DesktopTopbarBreadcrumb({ breadcrumb, pathname }) {
  const crumbs = topbarCrumbs(breadcrumb, pathname);
  return (
    <div className="hidden min-w-0 items-center gap-2 md:flex">
      <Link to="/internal" className="flex shrink-0 items-center gap-2">
        <LogoMark size={24} className="shrink-0" />
        <span className="font-display text-lg font-bold tracking-tight">idemGO</span>
        <span className="inline-flex items-center border border-zinc-200 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Internal
        </span>
      </Link>
      <span className="shrink-0 font-mono text-xs text-zinc-300">/</span>
      <ol className="flex min-w-0 items-center gap-2 text-sm">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <React.Fragment key={`${crumb.label}-${index}`}>
              {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-zinc-300" strokeWidth={1.8} />}
              <li className="min-w-0">
                {crumb.to && !isLast ? (
                  <Link to={crumb.to} className="text-zinc-400 hover:text-zinc-600">{crumb.label}</Link>
                ) : (
                  <span className={`${isLast ? 'font-medium text-zinc-900' : 'text-zinc-500'} block max-w-[32rem] truncate`} title={crumb.label}>
                    {crumb.label}
                  </span>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
}

export default function InternalAdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INTERNAL_SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const breadcrumbContext = useMemo(() => ({ breadcrumb, setBreadcrumb }), [breadcrumb]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(INTERNAL_SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? 'true' : 'false');
    } catch {
      // Ignore storage failures; the layout still works without persistence.
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <DashboardBreadcrumbProvider value={breadcrumbContext}>
      <div className={`min-h-screen ${APP_CANVAS_BG}`}>
        <header
          data-testid="internal-admin-header"
          className="fixed inset-x-0 top-0 z-40 h-16 border-b border-zinc-200 bg-white"
        >
          <div className="flex h-full items-center justify-between gap-3 px-4 md:px-6">
            <Link to="/internal" className="flex min-w-0 items-center gap-2 md:hidden" data-testid="internal-admin-brand">
              <LogoMark size={25} className="shrink-0" />
              <span className="font-display text-lg font-bold tracking-tight">idemGO</span>
              <span className="inline-flex items-center border border-zinc-200 bg-white px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Internal
              </span>
            </Link>
            <DesktopTopbarBreadcrumb breadcrumb={breadcrumb} pathname={location.pathname} />
            <div className="flex min-w-0 items-center gap-2 md:gap-3">
              <NotificationMenu />
              <AccountMenu
                user={user}
                className="hidden max-w-[240px] md:inline-flex"
                onNavigate={(path) => nav(path)}
                onLogout={() => { logout(); nav('/login'); }}
              />
              <button
                type="button"
                data-testid="internal-mobile-menu-toggle"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open navigation"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-zinc-200 hover:border-zinc-400 md:hidden"
              >
                <Menu className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </header>

        <aside
          data-testid="internal-admin-sidebar"
          className={`fixed left-0 top-16 bottom-0 z-30 hidden flex-col border-r border-zinc-100 bg-white py-6 transition-[width,padding] duration-200 ease-out md:flex ${sidebarCollapsed ? 'w-20 px-3' : 'w-52 px-4'}`}
        >
          <nav className="flex w-full flex-1 flex-col gap-1 overflow-y-auto" data-testid="internal-admin-nav">
            <NavLinks collapsed={sidebarCollapsed} />
          </nav>
          <div className={`mt-4 flex border-t border-zinc-100 pt-4 ${sidebarCollapsed ? 'justify-center' : 'justify-end'}`}>
            <button
              type="button"
              onClick={() => setSidebarCollapsed((current) => !current)}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-pressed={sidebarCollapsed}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="grid h-8 w-8 place-items-center rounded-sm border border-zinc-200 bg-white text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-900"
            >
              {sidebarCollapsed
                ? <ChevronRight className="h-4 w-4" strokeWidth={1.6} />
                : <ChevronLeft className="h-4 w-4" strokeWidth={1.6} />}
            </button>
          </div>
        </aside>

        {drawerOpen && (
          <div className="fixed inset-0 z-50 md:hidden" data-testid="internal-admin-mobile-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
            <aside className="absolute inset-y-0 right-0 flex w-64 flex-col border-l border-zinc-200 bg-white">
              <div className="flex h-16 items-center justify-between border-b border-zinc-200 px-4">
                <span className="font-display text-lg font-bold tracking-tight">idemGO</span>
                <button
                  type="button"
                  data-testid="internal-mobile-menu-close"
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close navigation"
                  className="grid h-9 w-9 place-items-center rounded-sm border border-zinc-200 hover:border-zinc-400"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-4" data-testid="internal-admin-mobile-nav">
                <NavLinks onNavigate={() => setDrawerOpen(false)} />
              </nav>
              <div className="space-y-3 border-t border-zinc-200 p-4" data-testid="internal-admin-mobile-footer">
                <div className="truncate font-mono text-xs text-zinc-500">{user?.email}</div>
                <button
                  onClick={() => { setDrawerOpen(false); logout(); nav('/login'); }}
                  data-testid="internal-mobile-logout-btn"
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-sm border border-zinc-200 px-3 py-2 text-sm text-zinc-700 transition-colors hover:border-zinc-400 hover:text-black"
                >
                  <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Logout
                </button>
              </div>
            </aside>
          </div>
        )}

        <main className={`min-h-screen ${APP_CANVAS_BG} pt-16 transition-[padding] duration-200 ease-out ${sidebarCollapsed ? 'md:pl-20' : 'md:pl-52'}`}>
          <div className="mx-auto max-w-[1440px] min-w-0 px-4 py-8 md:px-8">
            {user?.sandbox_mode && (
              <div data-testid="internal-sandbox-banner" className="mb-6 flex items-start gap-3 rounded-sm border border-amber-400 bg-amber-50 px-4 py-3">
                <FlaskConical className="mt-0.5 h-4 w-4 text-amber-700" strokeWidth={1.5} />
                <div className="text-sm text-amber-900">
                  <strong className="font-semibold">Sandbox mode is on.</strong> Internal simulations and workspace fixtures stay deterministic. No public login, OIDC, or regulated identifier behavior is added here.
                </div>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>
    </DashboardBreadcrumbProvider>
  );
}
